// Senha por aba (texto 10, D). Ajustes > Abas > [aba] > "Pedir senha ao abrir esta aba".
// Mesma regra do Diário: pede UMA vez ao entrar; dentro da aba não pede de novo; pede de novo só depois de
// sair (conforme a escolha: toda vez que sair, depois de 5 min fora, ou só quando o app bloquear).
// Galeria, câmera, arquivos e a biometria abertos pelo app não contam como sair (lib/ausencia: semBloqueio).
// A tela da trava é components/TravaAba.tsx; aqui fica só o estado (fora do React, para nunca mostrar a aba nem por um instante).
import { getDoc, setDoc } from '../store/store';
import { aoVoltar } from './ausencia';
import type { AbaId } from '../store/types';

export type RegraSenhaAba = 'sair' | '5min' | 'app';
const CINCO_MIN = 5 * 60000;
/** Abas em que o app sugere ligar a senha na primeira vez que abrem. */
export const ABAS_SUGERIR_SENHA: AbaId[] = ['consultorio', 'saude', 'documentos'];
/** Abas que NÃO usam esta trava (o Diário tem a própria; o My Planner é a tela inicial). */
export const ABAS_SEM_TRAVA: AbaId[] = ['diario', 'planner'];

type Aberta = { dentro: boolean; saiuEm: number };
const abertas = new Map<AbaId, Aberta>();
const trancouEm = new Map<AbaId, number>();
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => { try { f(); } catch { /* segue */ } }); };
export const assinarTravaAba = (f: () => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };
export const versaoTravaAba = () => versao;

/** Regra escolhida para a aba (null = não pede senha). */
export function regraSenhaAba(id: AbaId): RegraSenhaAba | null {
  if (ABAS_SEM_TRAVA.includes(id)) return null;
  try { return getDoc('ajustes')?.senhaAba?.[id] ?? null; } catch { return null; }
}

/** A aba está desbloqueada agora (ou não pede senha). */
export function abaAberta(id: AbaId): boolean {
  const r = regraSenhaAba(id);
  if (!r) return true;
  const e = abertas.get(id);
  if (!e) return false;
  if (e.dentro) return true;
  if (r === 'sair') return false;
  if (r === '5min') return Date.now() - e.saiuEm < CINCO_MIN;
  return true;   // 'app': só fecha quando o app bloquear
}
/** Abrir esta aba agora pediria a senha (o guia usa para avisar antes de destacar). */
export const precisaSenhaAba = (id: AbaId) => !abaAberta(id);
/** Quando a aba fechou pela última vez (0 = nunca abriu nesta execução do app). */
export const abaTrancouEm = (id: AbaId) => trancouEm.get(id) ?? 0;

function trancar(id: AbaId) {
  abertas.delete(id);
  trancouEm.set(id, Date.now());
  avisar();
}

// Quem quer saber quando a aba abrir (o guia: "Desbloqueie a aba e o guia continua daqui").
let depois: { id: AbaId; f: () => void; ate: number } | null = null;
export function aoDesbloquearAba(id: AbaId, f: () => void) { depois = { id, f, ate: Date.now() + 3 * 60000 }; }

/** Desbloqueou (senha, PIN, desenho ou biometria): a pessoa está dentro da aba. */
export function desbloquearAba(id: AbaId) {
  abertas.set(id, { dentro: true, saiuEm: 0 });
  avisar();
  const d = depois;
  if (d && d.id === id) { depois = null; if (Date.now() < d.ate) setTimeout(() => { try { d.f(); } catch { /* segue */ } }, 300); }
}

/** A aba voltou para a tela (foco). Se ainda vale, segue aberta; se venceu, fecha. */
export function entrarNaAba(id: AbaId) {
  const e = abertas.get(id);
  if (!e) return;
  if (abaAberta(id)) { if (!e.dentro) { e.dentro = true; avisar(); } }
  else trancar(id);
}

/** A aba saiu da tela (outra aba, Ajustes...). */
export function sairDaAba(id: AbaId) {
  const e = abertas.get(id);
  if (!e || !e.dentro) return;
  const r = regraSenhaAba(id);
  if (!r) return;
  if (r === 'sair') { trancar(id); return; }
  e.dentro = false;
  e.saiuEm = Date.now();
  // com a aba escondida, o conteúdo some sozinho quando passam os 5 minutos (não aparece nem por um instante na volta)
  if (r === '5min') setTimeout(() => { if (abertas.get(id) === e && !abaAberta(id)) trancar(id); }, CINCO_MIN + 100);
}

/** O app inteiro bloqueou (auth/AuthContext): as abas com "Só quando o app bloquear" fecham junto. */
export function aoBloquearAppAbas() {
  let mudou = false;
  for (const id of [...abertas.keys()]) if (regraSenhaAba(id) === 'app') { abertas.delete(id); trancouEm.set(id, Date.now()); mudou = true; }
  if (mudou) avisar();
}

// O app voltou para a tela depois de sair DE VERDADE (galeria, câmera, arquivos e biometria não contam).
aoVoltar(fora => {
  if (fora <= 0) return;
  for (const [id, e] of [...abertas]) {
    const r = regraSenhaAba(id);
    if (!r) continue;
    if (e.dentro && (r === 'sair' || (r === '5min' && fora >= CINCO_MIN))) { abertas.delete(id); trancouEm.set(id, Date.now()); }
    else if (!e.dentro && !abaAberta(id)) { abertas.delete(id); trancouEm.set(id, Date.now()); }
  }
  avisar();
});

/** Liga/desliga a senha da aba (ou muda a regra). Ligar estando dentro da aba já a deixa aberta. */
export function mudarSenhaAba(id: AbaId, regra: RegraSenhaAba | null, dentro = false) {
  if (regra && dentro) abertas.set(id, { dentro: true, saiuEm: 0 });
  if (!regra) abertas.delete(id);
  setDoc('ajustes', d => {
    const m = { ...(d.senhaAba ?? {}) };
    if (regra) m[id] = regra; else delete m[id];
    return { ...d, senhaAba: m };
  });
  avisar();
}

/** Marca que a sugestão "Quer proteger esta aba com senha?" já apareceu nesta aba. */
export function marcarSugestaoSenha(id: AbaId) {
  setDoc('ajustes', d => (d.senhaAbaSugerida?.includes(id) ? d : { ...d, senhaAbaSugerida: [...(d.senhaAbaSugerida ?? []), id] }));
}
